// @owners { team: patients }
import { BasePage, Button, Row, Toast, ToastContext, XlSpacing } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { rateShipmentDismissed } from '~shared/actions/rateShipment';
import { DEFAULT_ERROR_ALERT_MESSAGE, DEFAULT_ERROR_ALERT_TITLE } from '~shared/constants';
import { NO_RATING_ERROR_MESSAGE, createSurveyResponseDataPayload } from '~shared/features/rate-shipment/helpers';
import getPendingRating from '~shared/features/rate-shipment/selectors/getPendingRating';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { type SurveyResponsePayload, useSubmitSurveyResponse } from '~shared/queries/useSubmitSurveyResponse';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { RateShipment } from './RateShipment';

export const RateShipmentPage = () => {
  const { navigate } = useNavigation();
  const dispatch = useDispatchShared();
  const { mutateAsync: submitSurveyResponse } = useSubmitSurveyResponse();
  const { addToast } = useContext(ToastContext);
  const { trackEvent, trackPageView } = useAnalytics();
  const [rating, setRating] = useState(0);
  const [freeformReason, setFreeformReason] = useState('');
  const { uuid: uuidFromRoute } = useParams<{ uuid?: string }>();
  const pendingUuid = useSelectorShared(getPendingRating)?.uuid;
  const uuid = uuidFromRoute || pendingUuid;

  useEffect(() => {
    trackPageView({
      event: EVENTS.RATE_SHIPMENT__EXPERIENCE_VIEWED,
      params: { uuid },
    });
  }, [trackPageView, uuid]);

  const handleChangeRating = (rating: number) => {
    trackEvent({
      event: EVENTS.RATE_SHIPMENT__RATING_CLICKED,
      params: {
        rating,
        uuid,
      },
    });
    setRating(rating);
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      addToast(<Toast variant="error">{NO_RATING_ERROR_MESSAGE}</Toast>);
      return;
    }

    trackEvent({
      event: EVENTS.RATE_SHIPMENT__SUBMIT_CLICKED,
      params: { uuid },
    });

    const surveyResponse: SurveyResponsePayload = {
      name: 'rate_shipment',
      data: createSurveyResponseDataPayload(rating, freeformReason),
      survey_uuid: uuid ?? undefined,
      version: 2,
    };
    try {
      await submitSurveyResponse(surveyResponse);
      navigate('RouteAppTabNavigator', { screen: 'RouteTabMyMeds' });
      addToast(<Toast>Thank you for rating your delivery!</Toast>);
    } catch {
      addToast(
        <Toast variant="error">
          {DEFAULT_ERROR_ALERT_TITLE}. {DEFAULT_ERROR_ALERT_MESSAGE}
        </Toast>,
      );
    }
  };

  const handleDismiss = () => {
    trackEvent({
      event: EVENTS.RATE_SHIPMENT__DISMISS_CLICKED,
      params: { uuid },
    });
    // update redux store rateShipment.lastDismissedRatingUuid so patient can return to rate shipment
    dispatch(rateShipmentDismissed());
    navigate('RouteAppTabNavigator', { screen: 'RouteTabMyMeds' });
  };

  return (
    <BasePage>
      <XlSpacing />
      <RateShipment
        rating={rating}
        onRatingChange={handleChangeRating}
        freeformReason={freeformReason}
        onFreeformReasonChange={setFreeformReason}
        isModal={false}
      />
      <XlSpacing />
      <Row
        gap="XS"
        centerHorizontally
      >
        <Button
          label="Dismiss"
          accessibilityLabel="Press to close"
          type="secondary"
          onPress={handleDismiss}
        />
        <Button
          label="Submit"
          accessibilityLabel="Press to submit rating"
          onPress={handleSubmit}
        />
      </Row>
    </BasePage>
  );
};
